import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ResolverPatrocinioResponse } from '../../../../core/models/patrocinio.models';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { EstadoInvitacion, Postulacion } from '../../../../core/models/postulaciones.models';
import { PatrocinioService } from '../../../../core/services/patrocinio.service';
import { PostulacionesLogService } from '../../../../core/services/postulaciones-log.service';
import { PostulacionesService } from '../../../../core/services/postulaciones.service';
import { construirCsvPatrocinio, construirNombreDescargaPatrocinio } from './patrocinio-panel.export';
import { EmptyFilterState } from '../../../../shared/components/empty-filter-state/empty-filter-state';
import { TablePagination } from '../../../../shared/components/table-pagination/table-pagination';

@Component({
  selector: 'app-patrocinio-panel',
  imports: [CommonModule, FormsModule, EmptyFilterState, TablePagination],
  templateUrl: './patrocinio-panel.html',
  styleUrl: './patrocinio-panel.css',
})
export class PatrocinioPanel implements OnChanges {
  private static readonly BUSQUEDA_MINIMA = 4;
  private static readonly MAX_PALABRAS_COMENTARIO = 5000;

  @Input({ required: true }) rol: RolPlataforma = 'representante_institucional';

  postulaciones: Postulacion[] = [];
  postulacionesFiltradas: Postulacion[] = [];
  mensaje = '';
  mensajeBusqueda = '';
  mensajeDecision = '';
  busqueda = '';
  idPostulacionSeleccionado = 'Todos';
  folioSeleccionado = 'Todos';
  subdireccionSeleccionada = 'Todas';
  estadoSeleccionado = 'Todos';
  busquedaEjecutada = false;
  idsPostulacion: string[] = [];
  folios: string[] = [];
  subdirecciones: string[] = [];
  estados: string[] = ['Solicitada', 'Firmada', 'Observada', 'Rechazada'];
  modalDecisionVisible = false;
  modalResponderVisible = false;
  postulacionDecision: Postulacion | null = null;
  postulacionResponder: Postulacion | null = null;
  decisionPendiente: Extract<EstadoInvitacion, 'rechazada' | 'observada'> | null = null;
  comentarioDecision = '';
  readonly opcionesCantidad = [10, 20, 30, 40, 50];
  cantidadPorPagina = 10;
  paginaActual = 1;

  constructor(
    private readonly postulacionesService: PostulacionesService,
    private readonly patrocinioService: PatrocinioService,
    private readonly postulacionesLogService: PostulacionesLogService,
    private readonly router: Router
  ) {
    this.cargar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rol'] && !changes['rol'].firstChange) this.cargar();
  }

  get esRepresentanteInstitucional(): boolean {
    return this.rol === 'representante_institucional';
  }

  get esAyudanteRepresentante(): boolean {
    return this.rol === 'ayudante_representante';
  }

  registrarDecision(item: Postulacion, decision: Extract<EstadoInvitacion, 'aceptada' | 'rechazada' | 'observada'>): void {
    if (!this.esRepresentanteInstitucional) return;

    if (decision === 'aceptada') {
      this.aplicarDecision(item, decision);
      return;
    }

    this.abrirModalDecision(item, decision);
  }

  etiquetaEstado(item: Postulacion): string {
    if (item.firmaPatrocinioRealizada) return 'Firmada';
    if (item.estadoInvitacion === 'rechazada') return 'Rechazada';
    if (item.estadoInvitacion === 'observada') return 'Observada';
    return 'Solicitada';
  }

  puedeFirmarPatrocinio(item: Postulacion): boolean {
    return (
      this.rol === 'representante_institucional' &&
      item.solicitudPatrocinio &&
      !item.firmaPatrocinioRealizada &&
      item.estadoInvitacion !== 'rechazada' &&
      item.estadoInvitacion !== 'observada'
    );
  }

  tienePatrocinioPendienteFirma(item: Postulacion): boolean {
    return item.estadoPatrocinio === 'patrocinado' && !item.firmaPatrocinioRealizada;
  }

  tieneFirmaRegistrada(item: Postulacion): boolean {
    return item.firmaPatrocinioRealizada === true;
  }

  puedeResponderPatrocinio(item: Postulacion): boolean {
    return this.esRepresentanteInstitucional && !this.tieneFirmaRegistrada(item);
  }

  abrirModalResponder(item: Postulacion): void {
    if (!this.puedeResponderPatrocinio(item)) return;

    this.postulacionResponder = item;
    this.modalResponderVisible = true;
    this.mensajeDecision = '';
  }

  cerrarModalResponder(): void {
    this.modalResponderVisible = false;
    this.postulacionResponder = null;
    this.mensajeDecision = '';
  }

  aceptarDesdeRespuesta(): void {
    if (!this.postulacionResponder) return;
    const item = this.postulacionResponder;
    this.cerrarModalResponder();
    this.registrarDecision(item, 'aceptada');
  }

  firmarDesdeRespuesta(): void {
    if (!this.postulacionResponder) return;
    const item = this.postulacionResponder;
    this.cerrarModalResponder();

    if (item.estadoPatrocinio === 'patrocinado' || item.estadoInvitacion === 'aceptada') {
      this.irADetalleFirma(item);
      return;
    }

    this.registrarDecision(item, 'aceptada');
  }

  observarDesdeRespuesta(): void {
    if (!this.postulacionResponder) return;
    const item = this.postulacionResponder;
    this.cerrarModalResponder();
    this.abrirModalDecision(item, 'observada');
  }

  rechazarDesdeRespuesta(): void {
    if (!this.postulacionResponder) return;
    const item = this.postulacionResponder;
    this.cerrarModalResponder();
    this.abrirModalDecision(item, 'rechazada');
  }

  ejecutarBusqueda(): void {
    this.busquedaEjecutada = true;
    this.aplicarFiltros();
  }

  limpiarBusquedaYFiltros(): void {
    this.busqueda = '';
    this.idPostulacionSeleccionado = 'Todos';
    this.folioSeleccionado = 'Todos';
    this.subdireccionSeleccionada = 'Todas';
    this.estadoSeleccionado = 'Todos';
    this.busquedaEjecutada = false;
    this.mensajeBusqueda = '';
    this.aplicarFiltros();
  }

  abrirModalDecision(item: Postulacion, decision: Extract<EstadoInvitacion, 'rechazada' | 'observada'>): void {
    this.postulacionDecision = item;
    this.decisionPendiente = decision;
    this.comentarioDecision = '';
    this.mensajeDecision = '';
    this.modalDecisionVisible = true;
  }

  cerrarModalDecision(): void {
    this.modalDecisionVisible = false;
    this.postulacionDecision = null;
    this.decisionPendiente = null;
    this.comentarioDecision = '';
    this.mensajeDecision = '';
  }

  confirmarDecisionConComentario(): void {
    if (!this.postulacionDecision || !this.decisionPendiente) return;

    const palabras = this.contarPalabras(this.comentarioDecision);
    if (palabras === 0) {
      this.mensajeDecision = 'Debes ingresar un comentario para continuar.';
      return;
    }

    if (palabras > PatrocinioPanel.MAX_PALABRAS_COMENTARIO) {
      this.mensajeDecision = `El comentario supera el maximo de ${PatrocinioPanel.MAX_PALABRAS_COMENTARIO} palabras.`;
      return;
    }

    this.aplicarDecision(this.postulacionDecision, this.decisionPendiente, this.comentarioDecision);
    this.cerrarModalDecision();
  }

  irADetalleFirma(item: Postulacion): void {
    this.router.navigate(['/postulaciones/detalle', item.idPostulacion], {
      queryParams: {
        role: this.rol,
        from: 'patrocinio',
        firma: 'true',
      },
    });
  }

  get mostrarSinResultados(): boolean {
    if (!this.busquedaEjecutada) return false;
    if (this.busqueda.trim().length < PatrocinioPanel.BUSQUEDA_MINIMA) return false;
    return this.postulacionesFiltradas.length === 0;
  }

  get postulacionesPaginadas(): Postulacion[] {
    const paginaTotal = this.totalPaginas;
    if (this.paginaActual > paginaTotal) {
      this.paginaActual = paginaTotal;
    }

    const inicio = (this.paginaActual - 1) * this.cantidadPorPagina;
    return this.postulacionesFiltradas.slice(inicio, inicio + this.cantidadPorPagina);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.postulacionesFiltradas.length / this.cantidadPorPagina));
  }

  cambiarPagina(pagina: number): void {
    this.paginaActual = pagina;
  }

  cambiarCantidadDesdePaginador(cantidad: number): void {
    this.cantidadPorPagina = cantidad;
    this.paginaActual = 1;
  }

  get tituloModalDecision(): string {
    if (this.decisionPendiente === 'observada') return 'Observar postulacion';
    if (this.decisionPendiente === 'rechazada') return 'Rechazar postulacion';
    return 'Decision de patrocinio';
  }

  get etiquetaBotonDecision(): string {
    if (this.decisionPendiente === 'observada') return 'Observar';
    if (this.decisionPendiente === 'rechazada') return 'Rechazar';
    return 'Confirmar';
  }

  get placeholderComentarioDecision(): string {
    if (this.decisionPendiente === 'observada') {
      return 'Ingresa la observacion que debe corregir el postulante.';
    }
    return 'Ingresa el motivo o comentario del rechazo.';
  }

  get palabrasComentarioDecision(): number {
    return this.contarPalabras(this.comentarioDecision);
  }

  descargarHistorial(): void {
    const csv = construirCsvPatrocinio(this.postulacionesFiltradas, (item) => this.etiquetaEstado(item), (item) => this.obtenerIdProyecto(item));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fecha = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = construirNombreDescargaPatrocinio(fecha);
    link.click();

    URL.revokeObjectURL(url);

    this.mensaje = `Descarga generada con ${this.postulacionesFiltradas.length} registros filtrados.`;
    this.postulacionesLogService.registrar({
      timestamp: new Date().toISOString(),
      rol: this.rol,
      idPostulacion: '-',
      folio: '-',
      accion: 'descargar',
      detalle: `Descarga CSV de patrocinio con ${this.postulacionesFiltradas.length} registros filtrados.`,
    });
  }

  private cargar(): void {
    this.postulacionesService.getMisPostulaciones().subscribe((data) => {
      const base = data.filter((item) => item.rolesPermitidos.includes(this.rol) && item.solicitudPatrocinio);
      this.postulaciones = this.patrocinioService.aplicarEstadoPatrocinio(base);
      this.idsPostulacion = [...new Set(this.postulaciones.map((item) => item.idPostulacion))];
      this.folios = [...new Set(this.postulaciones.map((item) => item.folio))];
      this.subdirecciones = [...new Set(this.postulaciones.map((item) => item.subdireccion))];
      this.aplicarFiltros();
    });
  }

  private aplicarFiltros(): void {
    this.paginaActual = 1;
    this.mensajeBusqueda = '';
    const texto = this.busqueda.trim().toLowerCase();

    if (texto.length > 0 && texto.length < PatrocinioPanel.BUSQUEDA_MINIMA) {
      this.mensajeBusqueda = 'La busqueda requiere minimo 4 caracteres.';
    }

    this.postulacionesFiltradas = this.postulaciones.filter((item) => {
      const estado = this.etiquetaEstado(item);
      const matchIdPostulacion = this.idPostulacionSeleccionado === 'Todos' || item.idPostulacion === this.idPostulacionSeleccionado;
      const matchFolio = this.folioSeleccionado === 'Todos' || item.folio === this.folioSeleccionado;
      const matchSubdireccion = this.subdireccionSeleccionada === 'Todas' || item.subdireccion === this.subdireccionSeleccionada;
      const matchEstado = this.estadoSeleccionado === 'Todos' || estado === this.estadoSeleccionado;
      const matchTexto =
        texto.length === 0 ||
        texto.length < PatrocinioPanel.BUSQUEDA_MINIMA ||
        item.idPostulacion.toLowerCase().includes(texto) ||
        item.concurso.toLowerCase().includes(texto) ||
        item.folio.toLowerCase().includes(texto) ||
        item.nombreProyecto.toLowerCase().includes(texto);

      return matchIdPostulacion && matchFolio && matchSubdireccion && matchEstado && matchTexto;
    });
  }

  private aplicarDecision(
    item: Postulacion,
    decision: Extract<EstadoInvitacion, 'aceptada' | 'rechazada' | 'observada'>,
    comentario?: string
  ): void {
    const comentarioLimpio = comentario?.trim() || undefined;
    const solicitud$ =
      decision === 'aceptada'
        ? this.patrocinioService.aceptarPatrocinio(item.idPostulacion, { rol: this.rol })
        : decision === 'rechazada'
          ? this.patrocinioService.rechazarPatrocinio(item.idPostulacion, { rol: this.rol, comentario: comentarioLimpio })
          : this.patrocinioService.observarPatrocinio(item.idPostulacion, { rol: this.rol, comentario: comentarioLimpio });

    solicitud$.subscribe({
      next: (response) => this.aplicarResultadoDecision(item, decision, response, comentarioLimpio),
      error: () => {
        const mensajeError = 'No fue posible registrar la decision de patrocinio.';
        if (decision === 'aceptada') {
          this.mensaje = mensajeError;
        } else {
          this.mensajeDecision = mensajeError;
        }
      },
    });
  }

  private aplicarResultadoDecision(
    item: Postulacion,
    decision: Extract<EstadoInvitacion, 'aceptada' | 'rechazada' | 'observada'>,
    response: ResolverPatrocinioResponse,
    comentario?: string
  ): void {
    this.postulaciones = this.postulaciones.map((postulacion) =>
      postulacion.idPostulacion === response.idPostulacion
        ? this.patrocinioService.aplicarRespuesta(postulacion, response)
        : postulacion
    );
    this.postulaciones = this.patrocinioService.aplicarEstadoPatrocinio(this.postulaciones);
    this.aplicarFiltros();

    if (decision === 'aceptada') {
      this.mensaje = `Postulacion ${item.idPostulacion}: solicitud lista para firma institucional.`;
      this.irADetalleFirma(item);
      return;
    }

    this.mensaje =
      decision === 'observada'
        ? `Postulacion ${item.idPostulacion}: observada y enviada al postulante para correccion.`
        : `Postulacion ${item.idPostulacion}: rechazada y bloqueada para edicion.`;

    this.postulacionesLogService.registrar({
      timestamp: new Date().toISOString(),
      rol: this.rol,
      idPostulacion: item.idPostulacion,
      folio: item.folio,
      accion: 'solo_lectura',
      detalle: comentario
        ? `Patrocinio: ${this.etiquetaDecision(decision)}. Comentario: ${comentario}`
        : `Patrocinio: ${this.etiquetaDecision(decision)}.`,
    });
  }

  private etiquetaDecision(decision: Extract<EstadoInvitacion, 'aceptada' | 'rechazada' | 'observada'>): string {
    if (decision === 'aceptada') return 'aceptada';
    if (decision === 'rechazada') return 'rechazada';
    return 'observada';
  }

  private obtenerIdProyecto(item: Postulacion): string {
    return `${item.idPostulacion.replace('P-', 'PRJ-')}`;
  }

  private contarPalabras(texto: string): number {
    const normalizado = texto.trim();
    if (!normalizado) return 0;
    return normalizado.split(/\s+/).length;
  }
}
